// frontend/src/config/api.js
// In production/static builds, use relative URLs so nginx can proxy
// In development, use same-origin URLs and let Vite proxy /api to backend

// Override with VITE_API_URL when targeting custom environments.
// Default to same-origin and rely on reverse proxy (Vite in dev, Traefik/nginx in deploy).
const rawApiBaseUrl = (import.meta.env.VITE_API_URL || '').trim();

// Clinical Outcomes Service base URL.
// When CO is deployed separately, set VITE_CO_API_URL to point at it.
// When unset, falls back to the same origin (useful if CO is proxied under /co/ path).
const rawCoApiBaseUrl = (import.meta.env.VITE_CO_API_URL || '').trim();
const CO_API_VERSION = '/api/v1';
export const CO_BASE = rawCoApiBaseUrl
  ? rawCoApiBaseUrl.replace(/\/+$/, '')
  : (typeof window !== 'undefined' ? window.location.origin.replace(':5173', ':3001') : 'http://localhost:3001');
const API_VERSION = '/api/v1';
const IS_DEV = !!import.meta.env.DEV;

const normalizeApiBaseUrl = (value: string) => {
  if (!value) return '';
  const withoutTrailingSlash = value.replace(/\/+$/, '');

  try {
    const parsed = new URL(withoutTrailingSlash, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    let normalizedPath = parsed.pathname.replace(/\/+$/, '');

    while (/(?:\/[a-z0-9-]+)?\/api\/v1$/i.test(normalizedPath)) {
      normalizedPath = normalizedPath.replace(/(?:\/[a-z0-9-]+)?\/api\/v1$/i, '');
    }

    if (!/^https?:\/\//i.test(withoutTrailingSlash)) {
      return normalizedPath;
    }

    const rebuilt = `${parsed.protocol}//${parsed.host}${normalizedPath}`;
    return rebuilt.replace(/\/+$/, '');
  } catch {
    let normalized = withoutTrailingSlash;
    while (/(?:\/[a-z0-9-]+)?\/api\/v1$/i.test(normalized)) {
      normalized = normalized.replace(/(?:\/[a-z0-9-]+)?\/api\/v1$/i, '');
    }
    return normalized;
  }
};

const API_BASE_URL = normalizeApiBaseUrl(rawApiBaseUrl);

const getClientSlugForPath = () => {
  if (typeof window === 'undefined') return '';
  const candidate = (localStorage.getItem('selected_client_slug') || localStorage.getItem('selected_client_id') || '').trim().toLowerCase();
  if (!candidate) return '';
  return /^[a-z0-9-]+$/.test(candidate) ? candidate : '';
};

const getApiFullBase = (includeTenantSlug = true) => {
  const slug = includeTenantSlug ? getClientSlugForPath() : '';
  if (IS_DEV) {
    return `${API_BASE_URL}${API_VERSION}`;
  }
  return slug ? `${API_BASE_URL}/${slug}${API_VERSION}` : `${API_BASE_URL}${API_VERSION}`;
};

export const getServerLLMBaseUrl = () => `${getApiFullBase()}/server-llm`;

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const getSelectedClientId = () => {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem('selected_client_id') || '').trim();
};

const getSelectedFacilityId = () => {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem('selected_facility_id') || '').trim();
};

const withScopeQueryParams = (url: string) => {
  try {
    const parsedUrl = typeof window !== 'undefined' ? new URL(url, window.location.origin) : new URL(url);
    const clientId = getSelectedClientId();
    const facilityId = getSelectedFacilityId();

    if (clientId && !parsedUrl.searchParams.has('client_id')) {
      parsedUrl.searchParams.set('client_id', clientId);
    }

    if (facilityId && isUuid(facilityId) && !parsedUrl.searchParams.has('facility_id')) {
      parsedUrl.searchParams.set('facility_id', facilityId);
    }

    if (facilityId && isUuid(facilityId) && !parsedUrl.searchParams.has('facility')) {
      parsedUrl.searchParams.set('facility', facilityId);
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
};

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  VERSION: API_VERSION,
  FULL_BASE: `${API_BASE_URL}${API_VERSION}`,
};

export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/authentication/login/',
    LOGOUT: '/authentication/logout/',
    REGISTER: '/authentication/register/',
    USER: '/authentication/user/',
    LOGOUT_ALL: '/authentication/logout-all/',
    MFA_SETUP: '/authentication/mfa/setup/',
    MFA_SETUP_CONFIRM: '/authentication/mfa/setup/confirm/',
    MFA_VERIFY: '/authentication/mfa/verify/',
    MFA_STATUS: '/authentication/mfa/status/',
    MFA_DISABLE: '/authentication/mfa/disable/',
  },
  CORE: {
    CONTACTS: '/core/contacts/',
    CONTACT_TYPES: '/core/contact-types/',
    CONTACT_METHODS: '/core/contact-methods/',
  },
  PROVIDERS: {
    PROVIDERS: '/providers/providers/',
    FACILITIES: '/providers/facilities/',
    SESSION_TYPES: '/providers/session-types/',
  },
  PATIENTS: {
    PATIENTS: '/patients/',
    BY_CLIENT_ID: '/patients/by-client-id/',
    LIST: '/patients/',
  },
  USERS: {
    LIST: '/authentication/users/',
    DETAIL: '/authentication/users/',
  },
  GROUPS: {
    LIST: '/authentication/groups/',
    DETAIL: '/authentication/groups/',
  },
  LETTERS: {
    GENERATE: '/letters/generate/',
    LIST: '/letters/letters/',
    DETAIL: '/letters/letters/',
    TEMPLATES: '/letters/templates/',
  },
  DICTATIONS: {
    LIST: '/letters/dictations/',
    CREATE: '/letters/dictations/',
    DETAIL: '/letters/dictations/',
    TRANSCRIBE: (id: number) => `/letters/dictations/${id}/transcribe/`,
    UPDATE_PATIENT: (id: number) => `/letters/dictations/${id}/update_patient/`,
    MARK_COMPLETED: (id: number) => `/letters/dictations/${id}/mark_completed/`,
    RESET_STATUS: (id: number) => `/letters/dictations/${id}/reset_status/`,
  },
  DOCUMENTS: {
    LIST: '/documents/',
    CREATE: '/documents/',
    CREATE_FROM_LETTER: '/documents/create_from_letter/',
    DETAIL: '/documents/',
    DOWNLOAD: '/documents/',
    CONTENT: '/documents/',
    TYPES: '/documents/document-types/',
  },
  XMPP: {
    TOKEN: '/xmpp/token/',
    AUTH: '/xmpp/auth/',
    PREBIND: '/xmpp/prebind/',
  },
  CHAT: {
    MESSAGES:   '/chat/messages/',
    SAVE:       '/chat/messages/save/',
    UPLOAD:     '/chat/messages/upload/',
  },
  AI: {
    SEARCH_PUBMED: '/ai/pubmed/search/',  // Fixed to match PublicationSearch.tsx usage
    SEARCH_TGA: '/ai/tga/search/',  // TGA ARTG medication search
    SEARCH_PBS: '/ai/pbs/search/',  // PBS Australian medicines search
    GENERATE_LETTER: '/letters/generate/',  // Using OpenAI GPT-4o-mini
    PROVIDERS: '/ai/providers/',
    CONFIG: '/ai/config/',
    MODELS: '/ai/models/',  // Get available AI models
    DISCOVER_MODELS: '/ai/providers/discover_models/',  // Discover OpenAI & Ollama models
    GET_CONFIG: '/ai/providers/get_config/',  // Get saved AI configuration
    SAVE_CONFIG: '/ai/providers/save_config/',  // Save AI configuration
  },
  ACCOUNTING: {
    CHART_OF_ACCOUNTS: '/accounting/chart-of-accounts/',
  },
  RESEARCH: {
    // These endpoints now live in the Clinical Outcomes service.
    // CO_BASE resolves to the CO portal URL (default: localhost:3001).
    SPONSORS: `${CO_BASE}${CO_API_VERSION}/research/sponsors/`,
    STUDIES: `${CO_BASE}${CO_API_VERSION}/research/studies/`,
    PARTICIPANTS: `${CO_BASE}${CO_API_VERSION}/research/participants/`,
    PUBLICATIONS: `${CO_BASE}${CO_API_VERSION}/publications/`,
    ON_STUDY_LOG: `${CO_BASE}${CO_API_VERSION}/research/on-study-log/`,
    RECRUITMENT_LOG: `${CO_BASE}${CO_API_VERSION}/research/recruitment-log/`,
    REIMBURSEMENTS: `${CO_BASE}${CO_API_VERSION}/research/reimbursements/`,
    STUDY_TYPES: `${CO_BASE}${CO_API_VERSION}/research/study-types/`,
  },
  EMAIL: {
    LIST: '/email/emails/',
    CREATE: '/email/emails/',
    DETAIL: '/email/emails/',
    SEND: '/email/emails/send/',
    DRAFTS: '/email/emails/drafts/',
    SENT: '/email/emails/sent/',
    TEMPLATES: '/email/templates/',
    SIGNATURES: '/email/signatures/',
    SETTINGS: '/email/settings/',
    ACCOUNTS: {
      LIST: '/email/accounts/',
      CREATE: '/email/accounts/',
      DETAIL: (id: string) => `/email/accounts/${id}/`,
      UPDATE: (id: string) => `/email/accounts/${id}/`,
      DELETE: (id: string) => `/email/accounts/${id}/`,
      TEST: (id: string) => `/email/accounts/${id}/test_connection/`,
      SET_DEFAULT: (id: string) => `/email/accounts/${id}/set_default/`,
      DEFAULT: '/email/accounts/default/',
      ACTIVE: '/email/accounts/active/',
    },
  },
  SIGNATURES: {
    LIST: '/signatures/',
    CREATE: '/signatures/',
    DETAIL: '/signatures/',
    UPDATE: '/signatures/',
    DELETE: '/signatures/',
    DEFAULT: '/signatures/default/',
    SET_DEFAULT: (id: string) => `/signatures/${id}/set_default/`,
  },
  TASKS: {
    LIST: '/tasks/',
    CREATE: '/tasks/',
    DETAIL: '/tasks/',
    UPDATE: '/tasks/',
    DELETE: '/tasks/',
    NOTES: '/tasks/',
    ATTACHMENTS: '/tasks/',
    HISTORY: '/tasks/',
  },
  USER_FILES: {
    LIST: '/user-files/',
    UPLOAD: '/user-files/upload/',
    DOWNLOAD: (filename: string) => `/user-files/${encodeURIComponent(filename)}/download/`,
    DELETE: (filename: string) => `/user-files/${encodeURIComponent(filename)}/`,
  },
};

// Helper function for making authenticated API calls
// Only the pre-login/bootstrap auth flow runs before a client is selected —
// everything else under /authentication/ (users/, groups/) is tenant-scoped
// admin data and must go through the normal client_id/X-Client-Slug scoping,
// same as any other endpoint. A blanket '/authentication/' prefix match here
// previously exempted those too, so client_id was never sent to them.
const AUTH_BOOTSTRAP_PREFIXES = [
  '/authentication/login/',
  '/authentication/logout/',
  '/authentication/logout-all/',
  '/authentication/register/',
  '/authentication/user/',
  '/authentication/mfa/',
];

export const apiCall = async (endpoint: string, options: RequestInit = {}, token?: string) => {
  const normalizedEndpoint = endpoint.replace(/^\/api\/v1/i, '');
  const isAuthBootstrap = AUTH_BOOTSTRAP_PREFIXES.some(p => normalizedEndpoint.startsWith(p));
  const isTenantContextBootstrap = normalizedEndpoint.startsWith('/core/tenant-context/');
  const useTenantPath = !isAuthBootstrap && !isTenantContextBootstrap;
  const shouldApplyScope = !isAuthBootstrap && !isTenantContextBootstrap;
  const clientSlug = getClientSlugForPath();
  const base = getApiFullBase(useTenantPath);
  const scopedUrl = shouldApplyScope ? withScopeQueryParams(`${base}${normalizedEndpoint}`) : `${base}${normalizedEndpoint}`;
  
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  // Only add Content-Type if body is not FormData (let browser set it for FormData)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }

  if (useTenantPath && clientSlug && !headers['X-Client-Slug']) {
    headers['X-Client-Slug'] = clientSlug;
  }

  const response = await fetch(scopedUrl, {
    ...options,
    headers,
  });

  // A 401 here means the server has already invalidated this token (expired,
  // revoked, etc.) — the client-side 30-minute inactivity timer in
  // AuthContext only catches *inactivity*, not this case, so without this the
  // app just sits on a dead session showing "Invalid token" errors per-page
  // instead of returning to the login screen. Login itself is excluded so a
  // wrong password doesn't get mistaken for an expired session.
  if (response.status === 401 && normalizedEndpoint !== '/authentication/login/') {
    window.dispatchEvent(new Event('auth:session-expired'));
  }

  return response;
};